// Top-level error boundary.
//
// Embedded apps render inside Shopify admin's iframe; when React throws
// during mount the iframe just goes blank (no Shopify-side error chrome).
// Without a boundary, every subtle Polaris-prop misuse or null-pointer
// means "look, blank page" with no error message to act on. Catching here
// renders a visible fallback with the message and stack.

import React from "react";

interface Props {
  children: React.ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to console so it shows up in devtools too. We don't have a
    // server-side error reporting pipeline yet; console is the fastest
    // way to triage in dev.
    // eslint-disable-next-line no-console
    console.error("Datacrow SPA crash:", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: 24,
            fontFamily:
              "ui-sans-serif,-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif",
            color: "#202223",
          }}
        >
          <h1 style={{ fontSize: 18, marginBottom: 8 }}>
            Datacrow crashed
          </h1>
          <p style={{ marginBottom: 12 }}>
            The app hit an error while rendering. Copy the details below and
            share them so we can fix this.
          </p>
          <pre
            style={{
              background: "#f6f6f7",
              padding: 12,
              borderRadius: 4,
              fontSize: 12,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {this.state.error.message}
            {"\n\n"}
            {this.state.error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
